import {
  Avatar,
  Box,
  Chip,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MenuView from "../../components/MenuView";
import SnackAlert from "../../components/SnackAlert";
import useApi from "../../hooks/useApi";
import useSnack from "../../hooks/useSnack";
import LogsTable from "./components/DataTable";

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const { loadApi } = useApi();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    let endpoint = `admin/login-logs?page=${page}&limit=${rowsPerPage}`;
    if (searchQuery) endpoint += `&search=${encodeURIComponent(searchQuery)}`;
    loadApi(endpoint, true, "get")
      .then((response) => {
        if (cancelled) return;
        const result = response.data;
        setLogs(result.data || []);
        setTotal(result.total || 0);
        setTotalPages(result.totalPages || 0);
      })
      .catch((e) => {
        if (cancelled) return;
        showSnack(e.message || "Error al cargar registros");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, rowsPerPage, searchQuery]);

  function doSearch() {
    const q = (searchTerm || "").trim();
    setPage(1);
    setSearchQuery(q);
  }

  // Debounce: ejecutar búsqueda 2s después de dejar de teclear
  useEffect(() => {
    const q = (searchTerm || "").trim();
    if (q === searchQuery) return;
    const timer = setTimeout(() => {
      doSearch();
    }, 2000);
    return () => clearTimeout(timer);
  }, [searchTerm, searchQuery]);

  return (
    <>
      <Stack>
        <MenuView>
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              mx: "auto",
              width: "97%",
            }}
          >
            {/* Header */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={2}
              mb={3}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="#161616"
                    lineHeight={1.2}
                  >
                    Registro de Accesos
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Historial de inicios de sesión
                  </Typography>
                </Box>
              </Stack>

              <TextField
                size="small"
                placeholder="Buscar por usuario..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") doSearch();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        size="small"
                        onClick={doSearch}
                        sx={{ p: 0.5 }}
                        aria-label="buscar"
                      >
                        <SearchIcon sx={{ color: "#b0951c", fontSize: 20 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 260,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                    bgcolor: "#fff",
                    "&:hover fieldset": { borderColor: "#FCD528" },
                    "&.Mui-focused fieldset": { borderColor: "#FCD528" },
                  },
                }}
              />
            </Stack>

            {/* Stats */}
            <Stack
              direction="row"
              spacing={1.5}
              mb={2.5}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
                label={
                  loading ? "Cargando..." : `${total} registros totales`
                }
                sx={{
                  bgcolor: "#FCD52825",
                  color: "#b0951c",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderRadius: 2,
                  "& .MuiChip-icon": { color: "#b0951c" },
                }}
              />
              {totalPages > 1 && (
                <Chip
                  label={`Página ${page} de ${totalPages}`}
                  variant="outlined"
                  sx={{
                    borderColor: "#e0e0e0",
                    color: "#666",
                    fontSize: "0.8rem",
                    borderRadius: 2,
                  }}
                />
              )}
            </Stack>

            {/* Table */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e8e8e8",
                overflow: "hidden",
              }}
            >
              <LogsTable logs={logs} loading={loading} />

              {/* Pagination */}
              {!loading && totalPages > 0 && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems="center"
                  px={2.5}
                  py={1.5}
                  sx={{
                    borderTop: "1px solid #f0f0f0",
                    bgcolor: "#fafafa",
                  }}
                  gap={1.5}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      Filas por página:
                    </Typography>
                    <Select
                      size="small"
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(e.target.value);
                        setPage(1);
                      }}
                      sx={{
                        fontSize: "0.8rem",
                        height: 32,
                        borderRadius: 1.5,
                        "& .MuiSelect-icon": { color: "#161616" },
                      }}
                    >
                      {[10, 20, 50].map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    shape="rounded"
                    sx={{
                      "& .Mui-selected": {
                        bgcolor: "#FCD528 !important",
                        color: "#161616",
                        fontWeight: 700,
                      },
                    }}
                  />
                </Stack>
              )}
            </Paper>
          </Box>
        </MenuView>
      </Stack>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}
